import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calculator, Clock, Briefcase, Wand2 } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import FooterCTA from "../components/FooterCTA";
import useDocumentMeta from "../lib/useDocumentMeta";
import { buildWhatsAppLink } from "../lib/constants";

const SERVICES = [
    { id: "logo-design", label: "Logo Design", base: 4999 },
    { id: "brand-identity", label: "Brand Identity", base: 14999 },
    { id: "social-media-kit", label: "Social Media Kit", base: 7999 },
    { id: "website-design", label: "Website Design", base: 39999 },
    { id: "packaging-design", label: "Packaging Design", base: 11999 },
    { id: "glow-sign-board", label: "Glow Sign Board", base: 8999 },
    { id: "acrylic-sign-board", label: "Acrylic Sign Board", base: 6999 },
    { id: "shop-signage", label: "Shop Signage", base: 9999 },
];

const SIZES = [
    { id: "startup", label: "Startup", mult: 1 },
    { id: "small", label: "Small Business", mult: 1.4 },
    { id: "medium", label: "Medium Business", mult: 2.0 },
    { id: "large", label: "Large Enterprise", mult: 3.5 },
];

const URGENCY = [
    { id: "standard", label: "Standard · 7 days", mult: 1.0 },
    { id: "express", label: "Express · 3 days", mult: 1.3 },
    { id: "urgent", label: "Urgent · 24 hours", mult: 1.7 },
];

const inr = (n) =>
    new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(Math.round(n));

const QuotePage = () => {
    useDocumentMeta({
        title: "Instant Price Estimate — Design & Printing | Raj Graphics Nagpur",
        description:
            "Get an instant price estimate for logo, branding, signage, packaging or website work from Raj Graphics in Nagpur.",
        canonical: "/quote",
    });

    const [service, setService] = useState("");
    const [size, setSize] = useState("");
    const [urgency, setUrgency] = useState("");

    const ready = service && size && urgency;

    const estimate = useMemo(() => {
        if (!ready) return null;
        const s = SERVICES.find((x) => x.id === service);
        const sz = SIZES.find((x) => x.id === size);
        const u = URGENCY.find((x) => x.id === urgency);
        const base = s.base * sz.mult * u.mult;
        const low = base * 0.85;
        const high = base * 1.15;
        return { low, high, service: s, size: sz, urgency: u };
    }, [ready, service, size, urgency]);

    const waLink = useMemo(() => {
        if (!estimate) return buildWhatsAppLink("Hi! I'd like an exact quote.");
        const text = `Hi Raj Graphics! 👋\n\nI'd like an exact quote based on the estimator:\n• Service: ${estimate.service.label}\n• Business size: ${estimate.size.label}\n• Urgency: ${estimate.urgency.label}\n• Estimated range: ${inr(estimate.low)} – ${inr(estimate.high)}\n\nLooking forward to your reply.`;
        return buildWhatsAppLink(text);
    }, [estimate]);

    const reset = () => {
        setService("");
        setSize("");
        setUrgency("");
    };

    return (
        <main data-testid="quote-page" className="bg-[#050505]">
            {/* Hero */}
            <section className="relative pt-36 pb-12 md:pt-44 md:pb-16 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gold/[0.07] blur-[140px] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Instant Quote
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl"
                        data-testid="quote-headline"
                    >
                        Get an <span className="gold-gradient-text">Instant Price Estimate</span>.
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-6 font-inter text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed"
                    >
                        Three quick questions and you'll see a transparent price range — no
                        sign‑up, no spam. Confirm exact pricing on WhatsApp.
                    </motion.p>
                </div>
            </section>

            {/* Estimator */}
            <section className="relative pb-20 md:pb-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                    <div
                        data-testid="quote-form"
                        className="lg:col-span-7 bg-white/[0.02] border border-white/10 rounded-2xl p-7 md:p-10"
                    >
                        {/* Step 1 */}
                        <div data-testid="quote-step-1" className="mb-8">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="w-8 h-8 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center">
                                    <Wand2 size={14} className="text-gold" />
                                </span>
                                <div>
                                    <div className="font-inter text-[10px] uppercase tracking-[0.25em] text-gold">
                                        Step 1
                                    </div>
                                    <div className="font-poppins font-medium text-white">
                                        Select Service
                                    </div>
                                </div>
                            </div>
                            <Select value={service} onValueChange={setService}>
                                <SelectTrigger
                                    data-testid="quote-select-service"
                                    className="bg-black/40 border border-white/10 rounded-xl px-4 py-6 text-white font-inter hover:border-gold/40 focus:border-gold focus:ring-0 data-[placeholder]:text-zinc-500"
                                >
                                    <SelectValue placeholder="What do you need?" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-950 border border-white/10 text-white max-h-72">
                                    {SERVICES.map((s) => (
                                        <SelectItem
                                            key={s.id}
                                            value={s.id}
                                            data-testid={`quote-service-${s.id}`}
                                            className="focus:bg-gold/10 focus:text-gold"
                                        >
                                            {s.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Step 2 */}
                        <div data-testid="quote-step-2" className="mb-8">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="w-8 h-8 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center">
                                    <Briefcase size={14} className="text-gold" />
                                </span>
                                <div>
                                    <div className="font-inter text-[10px] uppercase tracking-[0.25em] text-gold">
                                        Step 2
                                    </div>
                                    <div className="font-poppins font-medium text-white">
                                        Business Size
                                    </div>
                                </div>
                            </div>
                            <Select value={size} onValueChange={setSize}>
                                <SelectTrigger
                                    data-testid="quote-select-size"
                                    className="bg-black/40 border border-white/10 rounded-xl px-4 py-6 text-white font-inter hover:border-gold/40 focus:border-gold focus:ring-0 data-[placeholder]:text-zinc-500"
                                >
                                    <SelectValue placeholder="Pick the closest match" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-950 border border-white/10 text-white">
                                    {SIZES.map((s) => (
                                        <SelectItem
                                            key={s.id}
                                            value={s.id}
                                            data-testid={`quote-size-${s.id}`}
                                            className="focus:bg-gold/10 focus:text-gold"
                                        >
                                            {s.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Step 3 */}
                        <div data-testid="quote-step-3">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="w-8 h-8 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center">
                                    <Clock size={14} className="text-gold" />
                                </span>
                                <div>
                                    <div className="font-inter text-[10px] uppercase tracking-[0.25em] text-gold">
                                        Step 3
                                    </div>
                                    <div className="font-poppins font-medium text-white">
                                        Urgency
                                    </div>
                                </div>
                            </div>
                            <Select value={urgency} onValueChange={setUrgency}>
                                <SelectTrigger
                                    data-testid="quote-select-urgency"
                                    className="bg-black/40 border border-white/10 rounded-xl px-4 py-6 text-white font-inter hover:border-gold/40 focus:border-gold focus:ring-0 data-[placeholder]:text-zinc-500"
                                >
                                    <SelectValue placeholder="When do you need it?" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-950 border border-white/10 text-white">
                                    {URGENCY.map((s) => (
                                        <SelectItem
                                            key={s.id}
                                            value={s.id}
                                            data-testid={`quote-urgency-${s.id}`}
                                            className="focus:bg-gold/10 focus:text-gold"
                                        >
                                            {s.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <button
                            type="button"
                            onClick={reset}
                            data-testid="quote-reset-btn"
                            className="mt-8 font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-gold transition-colors"
                        >
                            Reset selections
                        </button>
                    </div>

                    {/* Estimate panel */}
                    <div
                        className="lg:col-span-5"
                        data-testid="quote-estimate-panel"
                    >
                        <div className="sticky top-28 rounded-2xl bg-gradient-to-b from-gold/[0.10] to-gold/[0.02] border border-gold/30 p-7 md:p-9">
                            <div className="flex items-center gap-2 mb-5">
                                <Calculator size={16} className="text-gold" />
                                <span className="font-inter text-[10px] uppercase tracking-[0.25em] text-gold">
                                    Estimated price range
                                </span>
                            </div>

                            {!ready ? (
                                <div data-testid="quote-empty-state">
                                    <div className="font-poppins text-white text-2xl md:text-3xl leading-tight">
                                        Pick the three options to see your estimate.
                                    </div>
                                    <p className="mt-4 font-inter text-sm text-zinc-400">
                                        Your range updates instantly as you select.
                                    </p>
                                </div>
                            ) : (
                                <motion.div
                                    key={`${service}-${size}-${urgency}`}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.35 }}
                                    data-testid="quote-result"
                                >
                                    <div
                                        className="font-poppins font-bold text-white text-3xl md:text-5xl tracking-tight"
                                        data-testid="quote-price-range"
                                    >
                                        <span className="gold-gradient-text">
                                            {inr(estimate.low)}
                                        </span>
                                        <span className="text-zinc-500 mx-2 text-2xl md:text-3xl">–</span>
                                        <span className="gold-gradient-text">
                                            {inr(estimate.high)}
                                        </span>
                                    </div>

                                    <ul className="mt-6 space-y-2.5 font-inter text-sm">
                                        <li className="flex items-baseline gap-3">
                                            <span className="font-inter text-[10px] uppercase tracking-[0.2em] text-zinc-500 w-20">Service</span>
                                            <span className="text-white">{estimate.service.label}</span>
                                        </li>
                                        <li className="flex items-baseline gap-3">
                                            <span className="font-inter text-[10px] uppercase tracking-[0.2em] text-zinc-500 w-20">Scale</span>
                                            <span className="text-white">{estimate.size.label}</span>
                                        </li>
                                        <li className="flex items-baseline gap-3">
                                            <span className="font-inter text-[10px] uppercase tracking-[0.2em] text-zinc-500 w-20">Urgency</span>
                                            <span className="text-white">{estimate.urgency.label}</span>
                                        </li>
                                    </ul>

                                    <p className="mt-6 font-inter text-xs text-zinc-500 leading-relaxed">
                                        Indicative range only. Final price depends on scope, deliverables and material choices — confirmed on the call.
                                    </p>
                                </motion.div>
                            )}

                            <a
                                href={waLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="quote-whatsapp-cta"
                                className={`mt-7 group inline-flex w-full items-center justify-center gap-2 font-medium font-inter px-7 py-3.5 rounded-full transition-all ${
                                    ready
                                        ? "bg-gold hover:bg-[#C5A030] text-black hover:shadow-[0_10px_40px_rgba(212,175,55,0.35)]"
                                        : "bg-white/5 border border-white/10 text-zinc-300 hover:border-gold/40 hover:text-gold"
                                }`}
                            >
                                Get Exact Quote on WhatsApp
                                <ArrowRight
                                    size={18}
                                    className="transition-transform group-hover:translate-x-1"
                                />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <FooterCTA />
        </main>
    );
};

export default QuotePage;
